import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMatchingDiagnostics } from '../../hooks/useAdminDashboard';
import Spinner from '../../components/common/Spinner';
import { routes } from '../../lib/routes';
import { MdOutlineArrowBack, MdRefresh, MdSearch, MdExpandMore, MdExpandLess } from 'react-icons/md';
import { CheckCircle, WarningCircle, Xmark, InfoCircle } from 'iconoir-react';
import { Tooltip } from '@radix-ui/themes';
import type { UserMatchingDiagnostics } from '../../services/adminDashboardService';

// Status indicator component
const StatusBadge = ({ status, label }: { status: 'healthy' | 'warning' | 'error' | boolean; label?: string }) => {
  const isGood = status === 'healthy' || status === true;
  const isWarning = status === 'warning';

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
      isGood ? 'bg-green-100 text-green-700' :
      isWarning ? 'bg-yellow-100 text-yellow-700' :
      'bg-red-100 text-red-700'
    }`}>
      <div className={`w-1.5 h-1.5 rounded-full ${
        isGood ? 'bg-green-500' : isWarning ? 'bg-yellow-500' : 'bg-red-500'
      }`}></div>
      {label}
    </span>
  );
};

// Score display component
const ScoreDisplay = ({ score, label }: { score: number; label?: string }) => {
  const percentage = Math.round(score * 100);
  const color = percentage >= 70 ? 'text-green-600' : percentage >= 40 ? 'text-yellow-600' : 'text-red-600';

  return (
    <span className={`font-mono text-sm ${color}`}>
      {label && <span className="text-gray-400 mr-1">{label}:</span>}
      {percentage}%
    </span>
  );
};

// Expandable row for user details
const UserRow = ({ user, isExpanded, onToggle }: {
  user: UserMatchingDiagnostics;
  isExpanded: boolean;
  onToggle: () => void;
}) => {
  const hasMultiVector = user.embeddings.multi_vector_complete >= 4;
  const hasBasicEmbeddings = user.embeddings.has_basic;
  const matchCount = user.matches.backend_count || user.matches.dynamo_matches.length;

  return (
    <>
      {/* Main row */}
      <tr
        className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${isExpanded ? 'bg-gray-50' : ''}`}
        onClick={onToggle}
      >
        <td className="px-4 py-3">
          <button className="text-gray-400 hover:text-gray-600">
            {isExpanded ? <MdExpandLess className="w-5 h-5" /> : <MdExpandMore className="w-5 h-5" />}
          </button>
        </td>
        <td className="px-4 py-3">
          <div>
            <p className="font-medium text-gray-800 text-sm">{user.name || 'No name'}</p>
            <p className="text-xs text-gray-500">{user.email}</p>
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="flex flex-col gap-1">
            <StatusBadge status={hasBasicEmbeddings} label={hasBasicEmbeddings ? 'Basic' : 'None'} />
            <StatusBadge status={hasMultiVector} label={`MV: ${user.embeddings.multi_vector_complete}/${user.embeddings.multi_vector_total}`} />
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="text-sm">
            <span className="font-medium text-gray-700">{user.intent_classification.inferred_intent || 'Unknown'}</span>
            {user.intent_classification.archetype && (
              <p className="text-xs text-gray-500">{user.intent_classification.archetype}</p>
            )}
          </div>
        </td>
        <td className="px-4 py-3">
          {user.dealbreakers.length > 0 ? (
            <Tooltip content={user.dealbreakers.join(', ')}>
              <span className="text-sm text-red-600 font-medium cursor-help">
                {user.dealbreakers.length} set
              </span>
            </Tooltip>
          ) : (
            <span className="text-sm text-gray-400">None</span>
          )}
        </td>
        <td className="px-4 py-3">
          <div className="flex flex-col gap-0.5">
            <ScoreDisplay score={user.temporal_weighting.activity_boost} />
            <span className="text-xs text-gray-400">{user.temporal_weighting.boost_reason}</span>
          </div>
        </td>
        <td className="px-4 py-3">
          <StatusBadge
            status={user.same_objective_blocking.enabled ? 'warning' : 'healthy'}
            label={user.same_objective_blocking.enabled ? 'Active' : 'Off'}
          />
        </td>
        <td className="px-4 py-3">
          <span className={`font-medium ${matchCount > 0 ? 'text-green-600' : 'text-gray-400'}`}>
            {matchCount}
          </span>
        </td>
      </tr>

      {/* Expanded details row */}
      {isExpanded && (
        <tr className="bg-gray-50 border-b border-gray-200">
          <td colSpan={8} className="px-4 py-4">
            <div className="grid grid-cols-3 gap-6">
              {/* Embeddings Detail */}
              <div className="bg-white p-4 rounded-lg border">
                <h4 className="font-semibold text-gray-700 mb-3 text-sm">Embeddings</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Total Count:</span>
                    <span className="font-medium">{user.embeddings.count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Types:</span>
                    <span className="font-medium">{user.embeddings.types.join(', ') || 'None'}</span>
                  </div>
                  <div className="mt-3">
                    <span className="text-gray-500 block mb-2">Multi-Vector Status:</span>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(user.embeddings.multi_vector_details).map(([dim, info]) => (
                        <div key={dim} className="flex items-center gap-2 text-xs">
                          <div className={`w-2 h-2 rounded-full ${info.req && info.off ? 'bg-green-500' : 'bg-red-500'}`}></div>
                          <span className="capitalize">{dim.replace('_', ' ')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Intent & Persona */}
              <div className="bg-white p-4 rounded-lg border">
                <h4 className="font-semibold text-gray-700 mb-3 text-sm">Intent & Persona</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Intent:</span>
                    <span className="font-medium">{user.intent_classification.inferred_intent || 'Unknown'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Archetype:</span>
                    <span className="font-medium">{user.intent_classification.archetype || 'None'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">User Type:</span>
                    <span className="font-medium">{user.intent_classification.user_type || 'None'}</span>
                  </div>
                  {user.persona && (
                    <>
                      <div className="mt-3">
                        <span className="text-gray-500 block">Requirements:</span>
                        <p className="text-xs text-gray-700 mt-1">{user.persona.requirements_preview || 'Not set'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500 block">Offerings:</span>
                        <p className="text-xs text-gray-700 mt-1">{user.persona.offerings_preview || 'Not set'}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Matches */}
              <div className="bg-white p-4 rounded-lg border">
                <h4 className="font-semibold text-gray-700 mb-3 text-sm">Matches ({matchCount})</h4>
                {matchCount === 0 ? (
                  <p className="text-sm text-gray-400">No matches found</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {/* Show bidirectional scores if available */}
                    {user.matches.bidirectional_scores.length > 0 ? (
                      user.matches.bidirectional_scores.slice(0, 5).map((match: any, idx: number) => (
                        <div key={idx} className="text-xs p-2 bg-gray-50 rounded">
                          <div className="flex justify-between items-center">
                            <span className="font-medium text-gray-700 truncate max-w-[120px]">
                              {match.matched_user_name || match.matched_user_id?.slice(0, 8)}
                            </span>
                            <span className="text-green-600 font-mono">
                              {Math.round((match.final_score || match.similarity_score) * 100)}%
                            </span>
                          </div>
                          {(match.forward_score || match.reverse_score) && (
                            <div className="flex gap-2 mt-1 text-gray-500">
                              <span>F: {Math.round((match.forward_score || 0) * 100)}%</span>
                              <span>R: {Math.round((match.reverse_score || 0) * 100)}%</span>
                            </div>
                          )}
                          {match.explanation && (
                            <p className="mt-1 text-gray-500 truncate">{match.explanation}</p>
                          )}
                        </div>
                      ))
                    ) : user.matches.backend_matches.length > 0 ? (
                      user.matches.backend_matches.slice(0, 5).map((match: any, idx: number) => (
                        <div key={idx} className="text-xs p-2 bg-gray-50 rounded">
                          <div className="flex justify-between items-center">
                            <span className="font-medium text-gray-700">
                              {match.matched_user_name || 'Unknown'}
                            </span>
                            <span className="text-green-600 font-mono">
                              {Math.round((match.compatibility_score || 0) * 100)}%
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      user.matches.dynamo_matches.slice(0, 5).map((match: any, idx: number) => (
                        <div key={idx} className="text-xs p-2 bg-gray-50 rounded">
                          <div className="flex justify-between items-center">
                            <span className="font-medium text-gray-700 truncate max-w-[120px]">
                              {match.matched_user_id?.slice(0, 8) || 'Unknown'}
                            </span>
                            <span className="text-green-600 font-mono">
                              {Math.round((match.similarity_score || 0) * 100)}%
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                    {matchCount > 5 && (
                      <p className="text-xs text-gray-400 text-center">+{matchCount - 5} more</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Temporal & Blocking Info */}
            <div className="mt-4 flex gap-6 text-sm">
              <div className="bg-white p-3 rounded-lg border flex-1">
                <span className="text-gray-500">Temporal Weighting:</span>
                <span className="ml-2 font-medium">
                  {user.temporal_weighting.days_inactive} days inactive,
                  {' '}{Math.round(user.temporal_weighting.activity_boost * 100)}% boost
                  {' '}({user.temporal_weighting.boost_reason})
                </span>
              </div>
              {user.same_objective_blocking.enabled && (
                <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200 flex-1">
                  <span className="text-yellow-700">Blocking:</span>
                  <span className="ml-2 font-medium text-yellow-800">
                    Same intent ({user.same_objective_blocking.intent}),
                    would block {user.same_objective_blocking.would_block.length} users
                  </span>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const AdminMatchingPage = () => {
  const navigate = useNavigate();
  const { data: diagnosticsData, isLoading, refetch, isFetching } = useMatchingDiagnostics();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [filterIntent, setFilterIntent] = useState<string>('all');

  const diagnostics = diagnosticsData?.result;
  const users = diagnostics?.result || [];
  const summary = diagnostics?.summary;

  // Filter users based on search and intent filter
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const matchesSearch = searchQuery === '' ||
        user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.user_id.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesIntent = filterIntent === 'all' ||
        user.intent_classification.inferred_intent === filterIntent;

      return matchesSearch && matchesIntent;
    });
  }, [users, searchQuery, filterIntent]);

  const toggleExpanded = (userId: string) => {
    setExpandedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  // Get unique intents for filter dropdown
  const uniqueIntents = useMemo(() => {
    const intents = new Set(users.map(u => u.intent_classification.inferred_intent).filter(Boolean));
    return Array.from(intents);
  }, [users]);

  return (
    <div className="px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(routes.adminHealth)}
            className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"
          >
            <MdOutlineArrowBack className="w-5 h-5" />
          </button>
          <div>
            <h3 className="!font-medium text-heading-500 mb-1">Matching Diagnostics</h3>
            <p className="text-sm text-body-400 font-medium mb-0">
              Complete matching data for all users including embeddings, intent, and scores.
            </p>
          </div>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 disabled:opacity-50"
        >
          <MdRefresh className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-xl border">
            <p className="text-sm text-gray-500">Total Users</p>
            <p className="text-2xl font-bold text-gray-800">{summary.total_users}</p>
          </div>
          <div className="bg-white p-4 rounded-xl border">
            <p className="text-sm text-gray-500">With Multi-Vector</p>
            <p className="text-2xl font-bold text-green-600">{summary.with_multi_vector}</p>
          </div>
          <div className="bg-white p-4 rounded-xl border">
            <p className="text-sm text-gray-500">With Basic Embeddings</p>
            <p className="text-2xl font-bold text-blue-600">{summary.with_basic_embeddings}</p>
          </div>
          <div className="bg-white p-4 rounded-xl border">
            <p className="text-sm text-gray-500">With Matches</p>
            <p className="text-2xl font-bold text-purple-600">{summary.with_matches}</p>
          </div>
        </div>
      )}

      {/* Intent Breakdown */}
      {summary?.intent_breakdown && Object.keys(summary.intent_breakdown).length > 0 && (
        <div className="bg-white p-4 rounded-xl border mb-6">
          <h4 className="font-semibold text-gray-700 mb-3">Intent Distribution</h4>
          <div className="flex flex-wrap gap-3">
            {Object.entries(summary.intent_breakdown).map(([intent, count]) => (
              <div key={intent} className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full">
                <span className="text-sm font-medium text-gray-700">{intent}</span>
                <span className="text-xs bg-gray-200 px-2 py-0.5 rounded-full">{count as number}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search and Filter */}
      <div className="flex gap-4 mb-4">
        <div className="relative flex-1">
          <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by name, email, or user ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <select
          value={filterIntent}
          onChange={(e) => setFilterIntent(e.target.value)}
          className="px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="all">All Intents</option>
          {uniqueIntents.map(intent => (
            <option key={intent} value={intent}>{intent}</option>
          ))}
        </select>
      </div>

      {/* User Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-10"></th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Embeddings</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Intent</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dealbreakers</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Activity Boost</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Blocking</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Matches</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                    {searchQuery || filterIntent !== 'all'
                      ? 'No users match your filters'
                      : 'No users found'}
                  </td>
                </tr>
              ) : (
                filteredUsers.map(user => (
                  <UserRow
                    key={user.user_id}
                    user={user}
                    isExpanded={expandedUsers.has(user.user_id)}
                    onToggle={() => toggleExpanded(user.user_id)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Legend */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg text-sm text-gray-600">
        <p className="font-medium mb-2">Legend:</p>
        <ul className="flex flex-wrap gap-x-6 gap-y-2">
          <li className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            Healthy / Active / Has Data
          </li>
          <li className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            Warning / Blocking Active
          </li>
          <li className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            Error / Missing Data
          </li>
          <li className="flex items-center gap-2">
            <span className="font-mono text-xs">MV</span>
            Multi-Vector Embeddings
          </li>
          <li className="flex items-center gap-2">
            <span className="font-mono text-xs">F/R</span>
            Forward/Reverse Score
          </li>
        </ul>
      </div>
    </div>
  );
};

export default AdminMatchingPage;
